import { type ElasticsearchQuery } from '@searchkit/api';
import memoize from 'lodash.memoize';

import { REPUBS } from '@cx-modules/constants';

import { BET_TYPE_LABELS } from '~/components/BettingInsights/components/BetTypeLabels/BetTypeLabels.tsx';
import { CheckboxFilter } from '~/components/BettingInsights/components/Filters/CheckboxFilter';
import { DateRefinement } from '~/components/BettingInsights/components/Filters/DateRefinement';
import { SliderFilter } from '~/components/BettingInsights/components/Filters/SliderFilter';
import {
	PositiveEv,
	styles,
} from '~/components/BettingInsights/components/PositiveValue';
import { Profile } from '~/components/BettingInsights/components/Profile';
import { Autocomplete } from '~/components/BettingInsights/components/Search';
import { trackEvBetClick } from '~/components/BettingInsights/tracking';
import {
	isOcPlusRepub,
	type MarketDisplayNames,
	type Q4Type,
	REPUB_COUNTRY,
} from '~/components/BettingInsights/types/index.ts';
import { BetButton } from '~/components/OddscheckerPlus/components/BetButton';
import { Progress } from '~/components/OddscheckerPlus/components/cxy';
import {
	positiveEvResponseFactory,
	type ValueBetSearchResponseBody,
} from '~/configuration/betting-insights/response-factories/positiveEvResponseFactory.ts';
import { featureFlags } from '~/providers/feature-flag-provider';
import { type Loggable } from '~/traits/Loggable';
import { type RequestContext } from '~/types/RequestContext';
import { getOddsByFormat } from '~/utils/getOddsByFormat';

import { FOOTBALL } from './constants/categoryGroupNames.ts';
import {
	EVENT_DISPLAY_NAME,
	eventDisplayNames,
} from './constants/eventDisplayNames.ts';
import { allWNBAQuery } from './queries/allWNBAQuery';
import { eplOnlySoccerQuery } from './queries/eplOnlySoccerQuery.ts';
import { lineReductionQuery } from './queries/lineReductionQuery';
import { positiveEvQuery } from './queries/positiveEvQuery.ts';
import { supportedCategoryGroupsQuery } from './queries/supportedCategoryGroupsQuery.ts';
import { positiveEvQueryFactory } from './query-factories/positiveEvQueryFactory.ts';
import { formatDate } from './utils/formatDate.ts';
import { BettingInsights } from './BettingInsightsConfig';
import {
	OC_CATEGORY_START,
	US_CATEGORY_END,
	US_CATEGORY_START,
} from './constants';
import {
	type BettingInsightsFacetAttribute,
	type PositiveEvIndex,
} from './types';

export const positiveEvIndex = 'search-ev-bets' as const;

export function positiveEvConfigFactory(
	this: Loggable,
	requestContext: RequestContext,
) {
	// eslint-disable-next-line @typescript-eslint/no-this-alias
	const context = this;
	return BettingInsights.withRequestContext<PositiveEvIndex>(requestContext, {
		elasticSearch: {
			facetAttributes() {
				// TODO: re-enable this when app updates on their side
				// const repub = this.repubConfig.name;
				// if (!isOcPlusRepub(repub)) {
				// 	return [];
				// }

				// const repubCountry = REPUB_COUNTRY[repub];
				const areInternalSearchToolsEnabled =
					this.featureFlags.find(
						flag =>
							flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS,
					)?.enabled ?? false;

				return [
					...(areInternalSearchToolsEnabled
						? ([
								{
									attribute: 'bookmakerCode',
									field: 'bookmakerCode',
									type: 'string',
								},
							] satisfies BettingInsightsFacetAttribute<PositiveEvIndex>[])
						: []),
					{
						attribute: 'aiProbability',
						field: 'aiProbability',
						type: 'numeric',
					},
					{
						attribute: 'eventDisplayName',
						field: 'eventDisplayName',
						type: 'string',
					},
					{ attribute: 'eventId', field: 'eventId', type: 'numeric' },
					{
						attribute: 'expectedValue',
						field: 'expectedValue',
						type: 'numeric',
					},
					{
						attribute: 'marketPropType',
						field: 'marketPropType',
						type: 'string',
					},
					// TODO: remove hardcoded repubs and re-enable this when app updates on their side
					// {
					// 	attribute: `marketDisplayNameMap.${repubCountry}`,
					// 	field: `marketDisplayNameMap.${repubCountry}.keyword`,
					// 	type: 'string',
					// },
					{
						attribute: `marketDisplayNameMap.uk`,
						field: `marketDisplayNameMap.uk.keyword`,
						type: 'string',
					},
					{
						attribute: `marketDisplayNameMap.us`,
						field: `marketDisplayNameMap.us.keyword`,
						type: 'string',
					},
					{ attribute: 'oddsDecimal', field: 'oddsDecimal', type: 'numeric' },
					{
						attribute: 'sportsData.participant.name',
						field: 'sportsData.participant.name.keyword',
						type: 'string',
					},
					{
						attribute: 'sportsData.participant.nickName',
						field: 'sportsData.participant.nickName.keyword',
						type: 'string',
					},
					{
						attribute: 'sportsData.participant.shortName',
						field: 'sportsData.participant.shortName.keyword',
						type: 'string',
					},
					{
						attribute: 'subeventId',
						field: 'subeventId',
						type: 'numeric',
					},
					{
						attribute: 'subeventStartDate',
						field: 'subeventStartDate',
						type: 'string',
					},
					{
						attribute: 'subeventStartTime',
						field: 'subeventStartTime',
						type: 'string',
					},
					{
						attribute: 'sportsData.historyCount',
						field: 'sportsData.historyCount',
						type: 'numeric',
					},
					{
						attribute: 'sportsData.hitRates.last10',
						field: 'sportsData.hitRates.last10',
						type: 'numeric',
					},
					{
						attribute: 'teamData.away.name',
						field: 'teamData.away.name.keyword',
						type: 'string',
					},
					{
						attribute: 'teamData.away.nickName',
						field: 'teamData.away.nickName.keyword',
						type: 'string',
					},
					{
						attribute: 'teamData.away.shortName',
						field: 'teamData.away.shortName.keyword',
						type: 'string',
					},
					{
						attribute: 'teamData.home.name',
						field: 'teamData.home.name.keyword',
						type: 'string',
					},
					{
						attribute: 'teamData.home.nickName',
						field: 'teamData.home.nickName.keyword',
						type: 'string',
					},
					{
						attribute: 'teamData.home.shortName',
						field: 'teamData.home.shortName.keyword',
						type: 'string',
					},
					{
						attribute: 'type',
						field: 'type',
						type: 'string',
					},
				];
			},
			getBaseFilters() {
				const baseFilters: ElasticsearchQuery[] = [positiveEvQuery];

				const soccerEplOnlyEnabled =
					this.featureFlags.find(
						flag => flag.name === featureFlags.SUBS__PLUS__SOCCER_EPL_ONLY,
					)?.enabled ?? false;
				if (soccerEplOnlyEnabled) {
					baseFilters.push(eplOnlySoccerQuery);
				}

				// Toggle filtering out certain markets of WNBA
				const allWNBAEnabled =
					this.featureFlags.find(
						flag => flag.name === featureFlags.SUBS__PLUS__ALL_WNBA,
					)?.enabled ?? false;
				if (allWNBAEnabled) {
					baseFilters.push(allWNBAQuery);
				}

				// Toggle line reduction for line bets
				const lineReductionIsEnabled =
					this.featureFlags?.find(
						flag => flag.name === featureFlags.SUBS__PLUS__LINE_REDUCTION,
					)?.enabled ?? false;
				if (lineReductionIsEnabled) {
					baseFilters.push(lineReductionQuery);
				}

				// Limit category groups to supported list
				const newCategoryGroupsEnabled =
					this.featureFlags.find(
						flag => flag.name === featureFlags.SUBS__PLUS__NEW_CATEGORY_GROUPS,
					)?.enabled ?? false;
				if (!newCategoryGroupsEnabled) {
					baseFilters.push(supportedCategoryGroupsQuery);
				}

				return baseFilters;
			},
			async beforeSearch(searchRequests) {
				const queryFactory = positiveEvQueryFactory(this);
				context.logger.debug('[positive-ev]: beforeSearch', {
					query: queryFactory(searchRequests[0]),
				});

				return searchRequests.map(queryFactory);
			},
			async afterSearch(_, searchResponses) {
				return await Promise.all(
					searchResponses.map(searchResponse =>
						positiveEvResponseFactory.call(
							context,
							this,
							searchResponse as ValueBetSearchResponseBody,
						),
					),
				);
			},
			results() {
				return {
					attributes: [
						'aiProbability',
						// 'aiProjection',
						'betDescription',
						'betId',
						'betName',
						'betTypeId',
						'bookmakerCode',
						'categoryGroupId',
						'categoryGroupName',
						'categoryId',
						'categoryName',
						'eventDisplayName',
						'eventId',
						'expectedValue',
						'expectedValueStatus',
						'line',
						'marketClassification',
						'marketDisplayName',
						'marketDisplayNameMap',
						'marketId',
						'marketName',
						'marketPropType',
						'oddsAmerican',
						'oddsDecimal',
						'oddsFractional',
						'parentBetId',
						'sportsData',
						'sportsbookProbability',
						'subeventId',
						'subeventName',
						'subeventStartDate',
						'subeventStartTime',
						'teamData',
						'type',
					],
				};
			},
			search() {
				const repub = this.repubConfig.name;
				if (!isOcPlusRepub(repub)) {
					return {
						attributes: [],
					};
				}

				const repubCountry = REPUB_COUNTRY[repub];
				// const areInternalSearchToolsEnabled =
				// 	this.featureFlags.find(
				// 		flag =>
				// 			flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS,
				// 	)?.enabled ?? false;
				return {
					attributes: [
						`marketDisplayNameMap.${repubCountry}`,
						'teamData.away.name',
						'teamData.away.nickName',
						'teamData.away.shortName',
						'teamData.home.name',
						'teamData.home.nickName',
						'teamData.home.shortName',
						'sportsData.participant.name',
						'sportsData.participant.nickName',
						'sportsData.participant.shortName',
						// ...(areInternalSearchToolsEnabled
						// 	? ([
						// 			'betId',
						// 			'betTypeId',
						// 			'bookmakerCode',
						// 			'eventId',
						// 			'subeventId',
						// 		] as const)
						// 	: []),
					],
					highlight_attributes: [
						`marketDisplayNameMap.${repubCountry}`,
						'teamData.away.name',
						'teamData.away.nickName',
						'teamData.away.shortName',
						'teamData.home.name',
						'teamData.home.nickName',
						'teamData.home.shortName',
						'sportsData.participant.name',
						'sportsData.participant.nickName',
						'sportsData.participant.shortName',
						// ...(areInternalSearchToolsEnabled
						// 	? ([
						// 			'betId',
						// 			'betTypeId',
						// 			'bookmakerCode',
						// 			'eventId',
						// 			'subeventId',
						// 		] as const)
						// 	: []),
					],
				};
			},
			sorting({ currentRefinements }) {
				const eventDisplayName = currentRefinements.find(
					refinement => refinement.attribute === EVENT_DISPLAY_NAME,
				)?.refinements[0].value;

				const repubEventDisplayNames = eventDisplayNames[this.repubConfig.name];

				const isSoccer =
					eventDisplayName &&
					repubEventDisplayNames[
						eventDisplayName as keyof typeof repubEventDisplayNames
					]?.categoryGroupName === 'FOOTBALL';

				return {
					...(isSoccer
						? {
								defaultOverride: {
									field: 'sportsbookProbability',
									label: 'Implied Prob High-Low',
									order: 'desc',
								},
							}
						: {}),
					options: [
						{
							field: 'expectedValue',
							label: 'Expected Value High-Low',
							order: 'desc',
						},
						{
							field: 'expectedValue',
							label: 'Expected Value Low-High',
							order: 'asc',
						},
						{
							field: 'aiProbability',
							label: 'AI Prob High-Low',
							order: 'desc',
						},
						{
							field: 'aiProbability',
							label: 'AI Prob Low-High',
							order: 'asc',
						},
						{
							field: 'sportsbookProbability',
							label: 'Im. Prob High-Low',
							order: 'desc',
						},
						{
							field: 'sportsbookProbability',
							label: 'Im. Prob Low-High',
							order: 'asc',
						},
						{
							field: 'subeventStartTime',
							label: 'Event Time',
							order: 'asc',
						},
						{
							field: 'oddsDecimal',
							label: 'Best Odds High-Low',
							order: 'desc',
						},
						{
							field: 'oddsDecimal',
							label: 'Best Odds Low-High',
							order: 'asc',
						},

						{
							field: 'sportsData.hitRates.last10',
							label: 'Hit Rate (Last 10) High-Low',
							order: 'desc',
						},
						{
							field: 'sportsData.hitRates.last10',
							label: 'Hit Rate (Last 10) Low-High',
							order: 'asc',
						},
					],
				};
			},
		},
		ui: {
			card: {
				styles,
				table: {
					body: {
						render({
							bookmakers,
							content,
							label,
							oddsFormat,
							resultIndex,
							styles,
							table,
						}) {
							return (
								<PositiveEv.Body
									bookmakers={bookmakers}
									content={content}
									label={label}
									oddsFormat={oddsFormat}
									resultIndex={resultIndex}
									styles={styles}
									table={table}
								/>
							);
						},
					},
					footer: {
						render({ content }) {
							const isSoccerEvTrendsEnabled =
								this.featureFlags.find(
									flag =>
										flag.name === featureFlags.SUBS__PLUS__SOCCER_EV_TRENDS,
								)?.enabled ?? false;
							if (
								content.categoryGroupName === 'FOOTBALL' &&
								!isSoccerEvTrendsEnabled
							) {
								return null;
							}

							const formatDateToTimezone = (date: string, format?: string) => {
								return formatDate(date, format, this.repubConfig);
							};

							return (
								<PositiveEv.Footer
									content={content}
									formatDate={formatDateToTimezone}
								/>
							);
						},
					},
					getContent({ result }) {
						return [result];
					},
					headings: {
						row: 3,
						content: [
							{
								key: 'aiProbability',
								render() {
									return 'AI Probability';
								},
								renderMobile() {
									return 'AI Prob';
								},
								tooltipText:
									'The % likelihood a projection will cover the consensus line based on our proprietary AI platform',
							},
							{
								key: 'sportsbookProbability',
								render() {
									return 'Implied Probability';
								},
								renderMobile() {
									return 'Im. Prob';
								},
								tooltipText:
									'Implied probability the bet will win based on the sportsbook with best odds',
							},
							{
								key: 'expectedValue',
								render() {
									return 'EV';
								},
								tooltipPosition: 'left',
								tooltipText:
									'The average ROI a bettor can expect for this bet based on our AI predictive model. >0% - Profitable Bet, <0% = Unprofitable Bet, >10% = Highly Profitable Bet',
								highlight: true,
								justifyContent: 'center',
							},
							{
								key: 'bestOdds',
								render() {
									return 'Best Odds';
								},
								mobileView: 'hide',
								justifyContent: 'center',
							},
						],
					},
					main: {
						render({ content, resourcesUrl }) {
							const repub = this.repubConfig.name;
							if (!isOcPlusRepub(repub)) {
								return null;
							}

							const repubCountry = REPUB_COUNTRY[repub];

							const q4PropsEnabled =
								this.featureFlags.find(
									flag => flag.name === featureFlags.SUBS__PLUS__Q4_PROPS,
								)?.enabled ?? false;

							return (
								<Profile
									betName={content.betName}
									line={content.line}
									marketClassification={content.marketClassification}
									marketDisplayName={content.marketDisplayNameMap[repubCountry]}
									marketPropType={content.marketPropType}
									resourcesUrl={resourcesUrl}
									sportsData={content.sportsData}
									teamData={content.teamData}
									{...(q4PropsEnabled && { type: content.type })}
								/>
							);
						},
					},
					cells: [
						{
							key: 'aiProbability',
							render({ content, resultIndex: _ }) {
								return (
									<Progress
										aria-label="AI Probability"
										value={Math.round(content.aiProbability * 100) / 100}
									/>
								);
							},
							alignItems: 'flex-end',
						},
						{
							key: 'sportsbookProbability',
							render({ content }) {
								return (
									<Progress
										aria-label="Implied Probability"
										value={
											Math.round(content.sportsbookProbability * 100) / 100
										}
									/>
								);
							},
							alignItems: 'flex-end',
						},
						{
							key: 'expectedValue',
							render({ content }) {
								return <>{content.expectedValue}%</>;
							},
							highlight: ({ content }) =>
								content.expectedValueStatus?.toLowerCase(),
							justifyContent: 'center',
						},
						{
							key: 'bestOdds',
							render({ bookmakers, content, oddsFormat }) {
								const repub = this.repubConfig.name;
								const bookmaker = bookmakers[content.bookmakerCode];
								if (!bookmaker) {
									return null;
								}

								const onClick = memoize(() => {
									trackEvBetClick({ bookmaker, result: content });
								});

								return (
									<BetButton
										aria-label={`Place a bet on ${content.betName} ${
											content.marketDisplayNameMap[
												repub.toLowerCase() as keyof MarketDisplayNames
											]
										}
										with ${bookmaker.bookmakerName} at odds of ${content.oddsAmerican}`}
										bet={content}
										bookieColour={bookmaker.brandColour || 'black'}
										bookieLogo={bookmaker.logoSmall}
										bookieName={bookmaker.bookmakerName}
										odds={getOddsByFormat(content, oddsFormat)}
										onClick={onClick}
										repub={repub}
										shouldHideLabelOnDesktop
									/>
								);
							},
							isFullWidthMobile: true,
							justifyContent: 'center',
						},
					],
				},
				title: {
					getFormatOverride({ result }) {
						if (result.categoryGroupName === 'FOOTBALL') {
							return '{eventDisplayName} | {teamData.home.shortName} vs {teamData.away.shortName} | {subeventStartTime}';
						}
					},
					defaultFormat:
						'{eventDisplayName} | {teamData.away.shortName} @ {teamData.home.shortName} | {subeventStartTime}',
				},
				wrapper({ children, config, content, result }) {
					return (
						<PositiveEv config={config} content={content} result={result}>
							{children}
						</PositiveEv>
					);
				},
			},
			initialUiState() {
				return {
					hitsPerPage: 10,
					range: {
						aiProbability:
							this.repubConfig.name === REPUBS.US.name ? '30:95' : '30:80',
					},
					refinementList: {
						eventDisplayName: [
							this.repubConfig.name === REPUBS.US.name ? 'NFL' : 'EPL',
						],
					},
					sortBy:
						this.repubConfig.name === REPUBS.US.name
							? 'search-ev-bets_expectedValue_desc'
							: 'search-ev-bets_sportsbookProbability_desc',
				};
			},
			refinements: {
				clearAll: {
					excludedAttributes: [],
				},
				components: [
					{
						attribute: 'bookmakerCode',
						getLabel() {
							return 'Bookmaker (Internal use only)';
						},
						render({ bookmakers }) {
							const areInternalSearchToolsEnabled =
								this.featureFlags.find(
									flag =>
										flag.name ===
										featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS,
								)?.enabled ?? false;

							if (!areInternalSearchToolsEnabled) {
								return null;
							}

							return (
								<CheckboxFilter
									attribute={this.attribute}
									isDisabled={this.isWalled}
									label={this.getLabel()}
									sortBy={['name:asc']}
									transformFieldLabel={({ count, label }) => {
										const bookmaker = bookmakers[label];
										return `${bookmaker?.bookmakerName ?? label} (${count})`;
									}}
								/>
							);
						},
					},
					{
						attribute: 'expectedValue',
						getLabel() {
							return 'Minimum EV';
						},
						render() {
							return (
								<SliderFilter
									attribute={this.attribute}
									data-analytics="minimumEv"
									isDisabled={this.isWalled}
									label="EV"
									max={200}
								/>
							);
						},
					},
					{
						attribute: 'aiProbability',
						getLabel() {
							return 'Minimum AI Probability';
						},
						render({ currentRefinements }) {
							const eventDisplayName = currentRefinements.find(
								refinement => refinement.attribute === 'eventDisplayName',
							)?.refinements[0].value;

							const repubEventDisplayNames =
								eventDisplayNames[this.repubConfig.name];

							const isSoccer =
								repubEventDisplayNames[
									eventDisplayName as keyof typeof repubEventDisplayNames
								]?.categoryGroupName === FOOTBALL;

							return (
								<SliderFilter
									attribute={this.attribute}
									data-analytics="minimumAi"
									isDisabled={this.isWalled}
									label="AI Probability"
									max={
										this.repubConfig.name === REPUBS.OC.name || isSoccer
											? 80
											: 95
									}
									min={1}
								/>
							);
						},
					},
					{
						attribute: 'sportsData.hitRates.last10',
						getLabel() {
							return 'Minimum Hit Rate (Last 10)';
						},
						render() {
							return (
								<SliderFilter
									attribute={this.attribute}
									data-analytics="minimumHitRate"
									isDisabled={this.isWalled}
									label="Hit Rate (Last 10)"
									max={100}
									step={10}
								/>
							);
						},
					},
					{
						attribute: 'subeventStartDate',
						getLabel() {
							return 'Dates';
						},
						render() {
							return (
								<DateRefinement
									attribute={this.attribute}
									isDisabled={this.isWalled}
									label={this.getLabel()}
								/>
							);
						},
					},
					{
						attribute: 'marketPropType',
						getLabel() {
							return this.repubConfig.name === REPUBS.US.name
								? 'Props'
								: 'Bets';
						},
						render() {
							const q4PropsEnabled =
								this.featureFlags.find(
									flag => flag.name === featureFlags.SUBS__PLUS__Q4_PROPS,
								)?.enabled ?? false;

							return (
								<CheckboxFilter
									attribute={this.attribute}
									isDisabled={this.isWalled}
									label={this.getLabel()}
									sortBy={['name:asc']}
									transformFieldLabel={({ label }) =>
										label.replace('Props', this.getLabel())
									}
									{...(q4PropsEnabled && { childrenPlacement: 'before' })}
								>
									{q4PropsEnabled && (
										<CheckboxFilter
											attribute="type"
											isDisabled={this.isWalled}
											showMore={false}
											transformFieldLabel={({ label }) =>
												BET_TYPE_LABELS[label as Q4Type]({
													repub: this.repubConfig.name,
												})
											}
										/>
									)}
								</CheckboxFilter>
							);
						},
					},
				],
			},
			results: {
				categories() {
					return {
						attribute: EVENT_DISPLAY_NAME,
						labels: eventDisplayNames[this.repubConfig.name],
						order: {
							start:
								this.repubConfig.name === REPUBS.US.name
									? US_CATEGORY_START
									: OC_CATEGORY_START,
							...(this.repubConfig.name === REPUBS.US.name && {
								end: US_CATEGORY_END,
							}),
						},
					};
				},
				labelledBy: '#bet-name',
				propsName: 'bets',
			},
			search: {
				render({ searchAttributes }) {
					const textSearchEnabled =
						this.featureFlags.find(
							f => f.name === featureFlags.SUBS__PLUS__TEXT_SEARCH,
						)?.enabled ?? false;

					if (!textSearchEnabled) {
						return null;
					}

					return (
						<Autocomplete
							disabled={this.isWalled}
							indexName={positiveEvIndex}
							repub={this.repubConfig.name}
							searchAttributes={searchAttributes}
						/>
					);
				},
			},
			sorting: {
				canSort() {
					return true;
				},
			},
		},
	});
}
